import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useScrollSpy from "../hooks/useScrollSpy";
import { useUser } from "../contexts/UserContext";
// Critical components
import DropdownIndicator from "./DropdownIndicator";
import HamburgerIcon from "./HamburgerIcon";
import BrandLogo from "./BrandLogo";

// Lazy loaded components for interaction only
const MegaMenu = lazy(() => import("./MegaMenu"));
const MobileMenu = lazy(() => import("./MobileMenu"));
// Signed-in only — kept out of the main bundle so logged-out visitors never fetch it
const NavUserControls = lazy(() => import("./NavUserControls"));

/**
 * Enhanced Navbar with Mega Dropdowns
 * A modern, motion-based navigation system that incorporates mega dropdowns
 * while preserving the original scrolling functionality
 */
const Navbar = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const intentToLeaveRef = useRef(false);
  const [pendingSection, setPendingSection] = useState(null);
  // Raw scroll position in a ref — reading it never triggers a re-render
  const scrollPosRef = useRef(0);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const navRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const autoScrollTimerRef = useRef(null);

  const isMobileMenuTransitioning = useRef(false);

  const sectionIds = useMemo(
    () => ["overview", "services", "features", "future", "appcenter"],
    []
  );

  // Reads from ref — stable reference, never needs to recreate
  const getCurrentNavbarHeight = useCallback(
    () => (scrollPosRef.current > 80 ? 64 : 96),
    []
  );

  // updateUrl=false: we own URL sync below via replaceState (avoids navigate re-renders during scroll)
  const activeSectionRaw = useScrollSpy(sectionIds, getCurrentNavbarHeight, 20, false, null);
  const homePaths = ["/", "/overview", "/services", "/features", "/future", "/appcenter"];
  const isRoot = homePaths.includes(location.pathname);
  const activeSectionFromSpy = isRoot
    ? (activeSectionRaw === "future" ? "features" : activeSectionRaw)
    : null;
  // pendingSection overrides scroll spy during programmatic scroll (like AppCenter's setActiveTab)
  const activeSection = pendingSection || activeSectionFromSpy;

  // Update URL as sections scroll into view using replaceState — no React Router re-renders,
  // no scroll jank. Reverts to / when back in the hero.
  const prevSpySectionRef = useRef(null);
  useEffect(() => {
    const prevSpySection = prevSpySectionRef.current;
    prevSpySectionRef.current = activeSectionRaw;
    if (!isRoot) return;
    if (activeSectionRaw === null) {
      // Only revert when the user actually scrolled back up to the hero
      // (spy transitioned section → null). On mount or arrival from another
      // page the spy STARTS at null — rewriting the URL to "/" then would
      // clobber a direct /features load before AppContent scrolls to it.
      if (prevSpySection === null) return;
      clearTimeout(autoScrollTimerRef.current);
      setPendingSection(null);
      if (window.location.pathname !== "/") {
        window.history.replaceState(null, "", "/");
      }
    } else {
      const target = `/${activeSectionRaw}`;
      if (window.location.pathname !== target) {
        window.history.replaceState(null, "", target);
      }
    }
  }, [activeSectionRaw, isRoot]);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      scrollPosRef.current = y;
      // Only re-render when crossing the compact/full threshold
      const nowScrolled = y > 80;
      setIsScrolled((prev) => (prev === nowScrolled ? prev : nowScrolled));
    };

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    const handleResize = () => {
      setActiveDropdown(null);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
      clearTimeout(dropdownTimeoutRef.current);
      clearTimeout(autoScrollTimerRef.current);
    };
  }, []);

  // Add document body listener to close dropdown when hovering outside navbar
  useEffect(() => {
    const handleBodyHover = (e) => {
      if (e.target === document.body) {
        setActiveDropdown(null);
      }
    };
    const handleDocLeave = () => setActiveDropdown(null);

    document.body.addEventListener("mouseenter", handleBodyHover);
    document.addEventListener("mouseleave", handleDocLeave);

    return () => {
      document.body.removeEventListener("mouseenter", handleBodyHover);
      document.removeEventListener("mouseleave", handleDocLeave);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setNavOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleNavClick = (e, path, sectionId) => {
    e.preventDefault();
    setActiveDropdown(null);

    // Off the home page the sections don't exist and replaceState wouldn't
    // notify the router — do a real navigation home; AppContent's
    // section-scroll effect takes over once the section mounts.
    if (!isRoot) {
      navigate(path);
      return;
    }

    window.history.replaceState(null, "", path);

    // Immediately reflect the click in the nav highlight (mirrors AppCenter's setActiveTab)
    const navSectionId = sectionId === "future" ? "features" : sectionId;
    setPendingSection(navSectionId);
    clearTimeout(autoScrollTimerRef.current);

    const section = document.getElementById(sectionId);
    if (section) {
      const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset;

      let targetY;
      if (sectionId === "overview") {
        const viewportWidth = window.innerWidth;
        const logoMargin = viewportWidth < 768 ? 160 : 224;
        targetY = Math.max(0, sectionPosition - logoMargin - 150);
      } else {
        targetY = Math.max(0, sectionPosition - getCurrentNavbarHeight() - 20);
      }

      window.scrollTo({ top: targetY, behavior: "smooth" });
    }

    // Release override after smooth scroll completes so spy takes over naturally
    autoScrollTimerRef.current = setTimeout(() => {
      setPendingSection(null);
    }, 1000);
  };

  // For mobile toggle
  const handleDropdownToggle = (dropdownName) => {
    // Don't allow rapid toggles
    if (isMobileMenuTransitioning.current) return;

    // Block any toggle operations temporarily
    isMobileMenuTransitioning.current = true;

    // If already active, close it
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
    } else {
      // First ensure any active dropdown is closed
      if (activeDropdown !== null) {
        setActiveDropdown(null);
        // Then open the new one after a slight delay
        setTimeout(() => {
          setActiveDropdown(dropdownName);
          // Reset the transitioning flag
          isMobileMenuTransitioning.current = false;
        }, 50);
      } else {
        // Just open the dropdown if none are open
        setActiveDropdown(dropdownName);
      }
    }

    // Reset the transitioning flag after a delay if not reset by the code above
    setTimeout(() => {
      isMobileMenuTransitioning.current = false;
    }, 100);
  };

  const handleMouseEnter = (dropdownName) => {
    clearTimeout(dropdownTimeoutRef.current);
    intentToLeaveRef.current = false;
    setIsHoveringDropdown(true);
    setActiveDropdown(dropdownName);
  };

  const handleMouseLeave = () => {
    if (!isHoveringDropdown) {
      intentToLeaveRef.current = true;
      dropdownTimeoutRef.current = setTimeout(() => {
        if (intentToLeaveRef.current) setActiveDropdown(null);
      }, 400);
    }
  };

  const handleDropdownMouseEnter = () => {
    clearTimeout(dropdownTimeoutRef.current);
    intentToLeaveRef.current = false;
    setIsHoveringDropdown(true);
  };

  const handleDropdownMouseLeave = () => {
    setIsHoveringDropdown(false);
    intentToLeaveRef.current = true;
    dropdownTimeoutRef.current = setTimeout(() => {
      if (intentToLeaveRef.current) setActiveDropdown(null);
    }, 400);
  };

  // Dropdown definitions and content
  const dropdowns = {
    solutions: {
      title: "Solutions",
      columns: [
        {
          title: "Core Capabilities",
          items: [
            {
              name: "Neural Networks",
              path: "/solutions/neural-networks",
              description: "Advanced neural architectures for enterprise",
            },
            {
              name: "Data Processing",
              path: "/solutions/data-processing",
              description: "Intelligent data handling at scale",
            },
            {
              name: "Predictive Analytics",
              path: "/solutions/predictive-analytics",
              description: "Future-focused business insights",
            },
            {
              name: "NLP Solutions",
              path: "/solutions/nlp",
              description: "Natural language understanding and generation",
            },
          ],
        },
        {
          title: "Industry Solutions",
          items: [
            {
              name: "Finance & Banking",
              path: "/industries/finance",
              description: "AI-driven financial optimization",
            },
            {
              name: "Healthcare",
              path: "/industries/healthcare",
              description: "Medical intelligence systems",
            },
            {
              name: "Retail & E-commerce",
              path: "/industries/retail",
              description: "Smart shopping experiences",
            },
            {
              name: "Manufacturing",
              path: "/industries/manufacturing",
              description: "Intelligent production systems",
            },
          ],
        },
        {
          title: "Deployment Models",
          items: [
            {
              name: "Cloud Integration",
              path: "/deployment/cloud",
              description: "Scalable cloud-based AI",
            },
            {
              name: "On-Premise Solutions",
              path: "/deployment/on-premise",
              description: "Secure local implementations",
            },
            {
              name: "Hybrid Systems",
              path: "/deployment/hybrid",
              description: "Best of both worlds",
            },
            {
              name: "Edge Computing",
              path: "/deployment/edge",
              description: "Intelligence at the edge",
            },
          ],
        },
      ],
      featured: {
        title: "Featured: NeuralOps Platform",
        description:
          "Our flagship AI operations platform combining performance, security, and scalability for enterprise needs.",
        imageSrc: "",
        videoSrc: "/videos/neuralops.webm",
        link: "/products/neuralops",
      },
    },
    products: {
      title: "Products",
      columns: [
        {
          title: "Enterprise Platforms",
          items: [
            {
              name: "NeuralOps",
              path: "/products/neuralops",
              description: "AI-integrated DevOps solution",
            },
            {
              name: "DataSynapse",
              path: "/products/datasynapse",
              description: "Advanced data processing platform",
            },
            {
              name: "SecureAI Vault",
              path: "/products/secureai-vault",
              description: "Privacy-first AI implementation",
            },
            {
              name: "IntelliFlow",
              path: "/products/intelliflow",
              description: "Workflow automation system",
            },
          ],
        },
        {
          title: "Business Solutions",
          items: [
            {
              name: "CustomerAI",
              path: "/products/customerai",
              description: "Intelligent customer engagement",
            },
            {
              name: "SupplyGuard",
              path: "/products/supplyguard",
              description: "Supply chain optimization",
            },
            {
              name: "MarketSense",
              path: "/products/marketsense",
              description: "Predictive market analysis",
            },
            {
              name: "TalentNexus",
              path: "/products/talentnexus",
              description: "HR intelligence platform",
            },
          ],
        },
        {
          title: "Developer Tools",
          items: [
            {
              name: "Neural SDK",
              path: "/products/neural-sdk",
              description: "Development toolkit for AI integration",
            },
            {
              name: "AI Sandbox",
              path: "/products/ai-sandbox",
              description: "Experimentation environment",
            },
            {
              name: "ModelForge",
              path: "/products/modelforge",
              description: "Custom model building platform",
            },
            {
              name: "API Gateway",
              path: "/products/api-gateway",
              description: "Unified API management",
            },
          ],
        },
      ],
      featured: {
        title: "New: IntelliFlow Automation",
        description:
          "Our latest release brings unprecedented workflow automation capabilities with adaptive AI learning.",
        imageSrc: "/app-images/intelliflow-bg.webp",
        link: "/products/intelliflow",
      },
    },
    resources: {
      title: "Resources",
      columns: [
        {
          title: "Learn",
          items: [
            {
              name: "Documentation",
              path: "/resources/docs",
              description: "Comprehensive product guides",
            },
            {
              name: "Tutorials",
              path: "/resources/tutorials",
              description: "Step-by-step learning",
            },
            {
              name: "Webinars",
              path: "/resources/webinars",
              description: "Expert-led online sessions",
            },
            {
              name: "Blog",
              path: "/resources/blog",
              description: "Insights and updates",
            },
          ],
        },
        {
          title: "Support",
          items: [
            {
              name: "Knowledge Base",
              path: "/support/knowledge-base",
              description: "Self-service answers",
            },
            {
              name: "Community Forum",
              path: "/support/forum",
              description: "Connect with peers",
            },
            {
              name: "Technical Support",
              path: "/support/technical",
              description: "Expert assistance",
            },
            {
              name: "Status Page",
              path: "/support/status",
              description: "System performance updates",
            },
          ],
        },
        {
          title: "Insights",
          items: [
            {
              name: "Case Studies",
              path: "/insights/case-studies",
              description: "Customer success stories",
            },
            {
              name: "White Papers",
              path: "/insights/white-papers",
              description: "In-depth research",
            },
            {
              name: "Industry Reports",
              path: "/insights/reports",
              description: "Market and trend analysis",
            },
            {
              name: "ROI Calculator",
              path: "/insights/roi-calculator",
              description: "Value assessment tool",
            },
          ],
        },
      ],
      featured: {
        title: "Featured: AI Readiness Assessment",
        description:
          "Take our interactive assessment to determine your organization's AI maturity and implementation roadmap.",
        imageSrc: "", // Clear broken jpg, video will take priority in FeaturedImage
        videoSrc: "/videos/assessment.webm",
        link: "/insights/ai-readiness",
      },
    },
  };

  return (
    <header
      className={`fixed w-full z-[100] bg-white shadow-md transition-all duration-300 ${
        isScrolled ? "h-16" : "h-24"
      }`}
      ref={navRef}
    >
      {/* Signed in: match the AppCenter header shell (full width, px-3 / md:px-6)
          so logo and avatar sit at the same side margins on both bars. */}
      <div
        className={`h-full flex items-center justify-between relative ${
          user
            ? "w-full px-3 md:px-6"
            : "max-w-[1600px] mx-auto px-4 sm:px-4 md:px-2 lg:px-2"
        }`}
      >
        {/* Logo with min-width for SVG */}
        <Link
          to="/"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          onMouseEnter={() => {
            setActiveDropdown(null);
            setIsLogoHovered(true);
          }}
          onMouseLeave={() => setIsLogoHovered(false)}
          className="flex items-center text-2xl font-extrabold tracking-tight text-[#402c7f] z-10"
          aria-label="Neuralward - Home"
        >
          <BrandLogo isHovered={isLogoHovered} />
        </Link>

        {/* Combined nav and actions container */}
        <div className="flex items-center">
          {/* Desktop Nav with dynamic gap spacing */}
          <nav className="hidden lg:flex gap-2 lg:gap-2 xl:gap-2 text-gray-700 font-medium">
            {/* Updated navigation items to use activeSection for highlighting */}
            <a
              href="#overview"
              onClick={(e) => handleNavClick(e, "/overview", "overview")}
              className={`px-2 lg:px-3 py-2 rounded-md hover:bg-brandPrimary/10 hover:text-brandAccent transition-all text-sm lg:text-base ${
                activeSection === "overview" ? "text-brandAccent bg-brandPrimary/10" : ""
              }`}
              onMouseEnter={() => setActiveDropdown(null)}
              aria-label="View Company Overview"
            >
              Overview
            </a>
            <a
              href="#services"
              onClick={(e) => handleNavClick(e, "/services", "services")}
              className={`px-2 lg:px-3 py-2 rounded-md hover:bg-brandPrimary/10 hover:text-brandAccent transition-all text-sm lg:text-base ${
                activeSection === "services" ? "text-brandAccent bg-brandPrimary/10" : ""
              }`}
              onMouseEnter={() => setActiveDropdown(null)}
              aria-label="View Our Services"
            >
              Services
            </a>
            <a
              href="#features"
              onClick={(e) => handleNavClick(e, "/features", "features")}
              className={`px-2 lg:px-3 py-2 rounded-md hover:bg-brandPrimary/10 hover:text-brandAccent transition-all text-sm lg:text-base ${
                activeSection === "features" ? "text-brandAccent bg-brandPrimary/10" : ""
              }`}
              onMouseEnter={() => setActiveDropdown(null)}
              aria-label="View Key Features"
            >
              Features
            </a>
            <a
              href="#appcenter"
              onClick={(e) => handleNavClick(e, "/appcenter", "appcenter")}
              className={`px-2 lg:px-3 py-2 rounded-md hover:bg-brandPrimary/10 hover:text-brandAccent transition-all text-sm lg:text-base ${
                activeSection === "appcenter" ? "text-brandAccent bg-brandPrimary/10" : ""
              }`}
              onMouseEnter={() => setActiveDropdown(null)}
              aria-label="Visit AppCenter Store"
            >
              AppCenter
            </a>

            {/* Dropdown sections with responsive styling */}
            {Object.keys(dropdowns).map((key) => (
              <div
                key={key}
                className="relative"
                onMouseEnter={() => handleMouseEnter(key)}
                onMouseLeave={handleMouseLeave}
              >
                <DropdownIndicator isActive={activeDropdown === key}>
                  <button
                    className={`flex items-center px-2 lg:px-3 py-2 rounded-md hover:bg-brandPrimary/10 hover:text-brandAccent transition-all ${
                      activeDropdown === key
                        ? "text-brandAccent bg-brandPrimary/10"
                        : ""
                    }`}
                    aria-expanded={activeDropdown === key}
                    aria-label={`Toggle ${dropdowns[key].title} menu`}
                    onClick={() => handleDropdownToggle(key)}
                    onMouseEnter={() => handleMouseEnter(key)}
                  >
                    <span className="relative text-sm lg:text-base">
                      {dropdowns[key].title}
                    </span>
                    <motion.svg
                      className="ml-1 w-4 h-4 lg:w-5 lg:h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      animate={{ rotate: activeDropdown === key ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </motion.svg>
                  </button>
                </DropdownIndicator>
              </div>
            ))}
          </nav>

          {/* Actions container - right aligned */}
          <div className="flex items-center">
            {/* Call to Action button with compact styling and no pulse effect */}
            <div className="hidden lg:block">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400 }}
                onMouseEnter={() => setActiveDropdown(null)}
              >
                <Link
                  to="/get-started"
                  className="ml-4 xl:ml-6 inline-block bg-brandPrimary hover:bg-brandAccent text-white py-2 px-5 lg:px-5 xl:px-5 md:px-3 rounded-md transition-colors duration-300 text-sm lg:text-base"
                  aria-label="Get Started with Neuralward"
                >
                  <span>Get Started</span>
                </Link>
              </motion.div>
            </div>

            {/* Notifications + avatar — signed-in users only */}
            {user && (
              <Suspense fallback={null}>
                <NavUserControls />
              </Suspense>
            )}
          </div>
        </div>

        {/* Mobile toggle - ABSOLUTELY POSITIONED */}
        <div className="lg:hidden absolute right-4 top-1/2 transform -translate-y-1/2">
          <HamburgerIcon
            isOpen={navOpen}
            onClick={() => setNavOpen(!navOpen)}
          />
        </div>
      </div>
      {/* Dropdown Menus (Desktop) */}
      <AnimatePresence>
        {activeDropdown && (
          <Suspense fallback={null}>
            <MegaMenu
              menu={dropdowns[activeDropdown]}
              onItemClick={() => setActiveDropdown(null)}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            />
          </Suspense>
        )}
      </AnimatePresence>
      {/* Mobile Menu */}
      <Suspense fallback={null}>
        <MobileMenu
          isOpen={navOpen}
          dropdowns={dropdowns}
          activeDropdown={activeDropdown}
          handleDropdownToggle={handleDropdownToggle}
          handleNavClick={handleNavClick}
          handleClose={() => setNavOpen(false)}
        />
      </Suspense>
    </header>
  );
};

export default Navbar;
